import type { RuntimeStatusResponse } from "./types";

type RuntimeStatusTone = "green" | "amber";

type RuntimeBudgetReview = NonNullable<RuntimeStatusResponse["xRumorBudget"]>["review"];
type RuntimeBudget = NonNullable<RuntimeStatusResponse["xRumorBudget"]>;
type RuntimeStatusLine = {
  tone: RuntimeStatusTone;
  title: string;
  counts: string;
  review: string;
};

type XaiQuotaEstimate = NonNullable<RuntimeStatusResponse["xaiQuotaEstimate"]>;

function budgetReasonLabel(reason: string | undefined, fallback: string) {
  if (!reason) return fallback;
  if (reason === "within_xai_budget") return "예산 여유";
  if (reason === "xai_manual_halt_active") return "수동 중지";
  if (reason.includes("rate") || reason.includes("quota")) return "제공자 제한";
  if (reason.includes("cooldown")) return "쿨다운";
  return reason.replaceAll("_", " ");
}

function budgetPressureLabel(pressure: string | undefined, fallback: string) {
  if (!pressure) return fallback;
  if (pressure === "normal") return "정상";
  if (pressure === "tight") return "절약 모드";
  if (pressure === "blocked") return "차단";
  return pressure.replaceAll("_", " ");
}

function roundedPercent(value: number | undefined) {
  return Number.isFinite(value) ? Math.round(Number(value)) : undefined;
}

function scanAvailabilityLabel(budget: RuntimeBudget | undefined) {
  if (budget?.allowed === false) return "스캔 제한";
  if (budget?.manualHalt) return "수동 중지";
  return "스캔 허용";
}

function summarizeXaiQuotaEstimate(
  estimate: XaiQuotaEstimate,
  budget: RuntimeBudget | undefined,
): RuntimeStatusLine {
  const days = estimate.projectedDaysRemaining;
  const fiveHourPct = roundedPercent(estimate.windows.fiveHour.percent);
  const dayPct = roundedPercent(estimate.windows.day.percent);
  const monthPct = roundedPercent(estimate.windows.month.percent);
  const counts =
    typeof days === "number" && Number.isFinite(days)
      ? `로컬 추정 ${Math.max(0, Math.floor(days))}일 남음`
      : "로컬 추정 불가";
  const details =
    fiveHourPct !== undefined && dayPct !== undefined && monthPct !== undefined
      ? `비권위 · 5h ${fiveHourPct}% / 24h ${dayPct}% / 30d ${monthPct}%`
      : "비권위 · 제공자 잔여량 미확인";
  return {
    tone: budget?.allowed === false || budget?.manualHalt ? "amber" : "green",
    title: "X Search 한도",
    counts,
    review: `${details} · ${scanAvailabilityLabel(budget)}`,
  };
}

const runtimeStatusLines = new Map<
  RuntimeBudgetReview,
  (budget: RuntimeBudget) => RuntimeStatusLine
>([
  [
    "manual_halt",
    () => ({
      tone: "amber",
      title: "X 소식 수동 중지",
      counts: "스캔 차단",
      review: "토큰 보호 중지",
    }),
  ],
  [
    "blocked",
    (budget) => ({
      tone: "amber",
      title: "X 소식 차단",
      counts: budgetReasonLabel(budget.reason, "예산 차단"),
      review: budgetPressureLabel(budget.pressure, "점검 필요"),
    }),
  ],
  [
    "tight",
    (budget) => ({
      tone: "amber",
      title: "X 소식 절약",
      counts: budgetReasonLabel(budget.reason, "예산 타이트"),
      review: budgetPressureLabel(budget.pressure, "간격 조절"),
    }),
  ],
  [
    "clear",
    (budget) => ({
      tone: "green",
      title: "X 소식 정상",
      counts: budgetReasonLabel(budget.reason, "예산 여유"),
      review: budgetPressureLabel(budget.pressure, "스캔 가능"),
    }),
  ],
  [
    "unknown",
    (budget) => ({
      tone: "amber",
      title: "X 소식 확인",
      counts: budgetReasonLabel(budget.reason, "상태 확인 중"),
      review: budgetPressureLabel(budget.pressure, "런타임 기록 대기"),
    }),
  ],
]);

function effectiveReview(status: RuntimeStatusResponse): RuntimeBudgetReview | undefined {
  const budget = status.xRumorBudget;
  if (!budget) return undefined;
  return budget.manualHalt ? "manual_halt" : budget.review;
}

export function summarizeRuntimeStatusOperatorLine(
  status?: RuntimeStatusResponse,
): RuntimeStatusLine {
  const budget = status?.xRumorBudget;
  const review = status ? effectiveReview(status) : undefined;
  if (!budget || !review) {
    return {
      tone: "amber",
      title: "X 소식 확인",
      counts: "상태 확인 중",
      review: "런타임 기록 대기",
    };
  }
  if (status?.xaiQuotaEstimate && review === "clear") {
    return summarizeXaiQuotaEstimate(status.xaiQuotaEstimate, budget);
  }
  return runtimeStatusLines.get(review)?.(budget) ?? runtimeStatusLines.get("unknown")!(budget);
}
